// src/pages/earningsImport/pos/POSUploadSection.js

import React, { useState } from "react";

function downloadCSV(filename, rows) {
  if (!rows || rows.length === 0) return;

  const headers = Object.keys(rows[0]);
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function POSUploadSection({
  month,
  setMonth,
  onFileSelect,
  onProcess,
}) {
  const [loading, setLoading] = useState(false);

  // ✅ Summary only (no POS data shown)
  const [result, setResult] = useState(null); // { savedCount, unmatchedCount, errors, unmatched, uploadId }

  async function handleProcessClick() {
    if (loading) return;

    setLoading(true);
    setResult(null);

    try {
      const res = await onProcess(); // IMPORTANT: onProcess should return summary object
      setResult(res || { ok: true });
    } catch (err) {
      console.error("Error running POS processing:", err);
      setResult({
        ok: false,
        savedCount: 0,
        unmatchedCount: 0,
        errors: [err?.message || String(err)],
      });
    }

    setLoading(false);
  }

  return (
    <div
      style={{
        marginTop: 25,
        padding: 16,
        border: "1px solid #ccc",
        borderRadius: 8,
        background: "#fafafa",
      }}
    >
      <h3 style={{ marginBottom: 12 }}>🧾 POS Transactions Upload</h3>

      {/* MONTH SELECTOR */}
      <label style={{ fontWeight: "600" }}>Select Month</label>
      <input
        type="month"
        value={month}
        onChange={(e) => setMonth(e.target.value)}
        style={{
          display: "block",
          marginBottom: 15,
          padding: 8,
          width: 220,
          borderRadius: 6,
          border: "1px solid #ccc",
        }}
      />

      {/* FILE UPLOAD */}
      <label style={{ fontWeight: "600" }}>Upload POS File</label>
      <input
        type="file"
        accept=".csv, .xlsx"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFileSelect(file);
        }}
        style={{
          marginBottom: 12,
          display: "block",
        }}
      />

      {/* PROCESS BUTTON WITH LOADING */}
      <button
        onClick={handleProcessClick}
        disabled={loading}
        style={{
          padding: "8px 16px",
          background: loading ? "#777" : "#2e7d32",
          color: "white",
          borderRadius: 6,
          border: "none",
          cursor: loading ? "not-allowed" : "pointer",
          fontWeight: 600,
          marginBottom: 12,
          minWidth: 160,
        }}
      >
        {loading ? "Processing…" : "Process POS File"}
      </button>

      {/* ✅ SUMMARY ONLY (no POS data) */}
      {result && (
        <div
          style={{
            padding: 12,
            borderRadius: 8,
            border: "1px solid #ddd",
            background: "white",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            {result.ok === false ? "❌ Upload Result" : "✅ Upload Result"}
          </div>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12, color: "#666" }}>Saved</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {result.savedCount ?? "-"}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#666" }}>Duplicates</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {result.duplicateCount ?? 0}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#666" }}>Unmatched</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {result.unmatchedCount ?? 0}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#666" }}>Errors</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {Array.isArray(result.errors) ? result.errors.length : 0}
              </div>
            </div>

            {result.uploadId && (
              <div>
                <div style={{ fontSize: 12, color: "#666" }}>Upload ID</div>
                <div style={{ fontSize: 12, fontFamily: "monospace" }}>
                  {result.uploadId}
                </div>
              </div>
            )}
          </div>

          {/* Download unmatched evidence */}
          {Array.isArray(result.unmatched) && result.unmatched.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <button
                onClick={() =>
                  downloadCSV(
                    `POS_unmatched_${month || "month"}.csv`,
                    result.unmatched
                  )
                }
                style={{
                  padding: "7px 12px",
                  background: "#b45309",
                  color: "white",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Download Unmatched CSV
              </button>
              <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
                (Only terminal/date/amount/reason — no POS details screen.)
              </div>
            </div>
          )}

          {/* Show first error message only (no sensitive data) */}
          {Array.isArray(result.errors) && result.errors.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: "#b91c1c" }}>
              {result.errors[0]}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
